// The dealer's notification feed: their own alerts (users/{uid}/notifications)
// merged with GharSetu broadcasts meant for dealers, the same sources the
// app's notification screen shows. "New" is derived the way the app derives
// it too: anything sent after this device last opened the list, because
// broadcasts are shared docs with no per-user read flag.

const SEEN_KEY_PREFIX = "gs-notifications-seen:";

// Broadcast states that reached people; drafts and failed sends never did.
const DELIVERED = new Set(["SENT", "IN_APP_ONLY"]);

// Cloud Functions write these titles in Title Case; the portal is sentence case.
const TITLE_FIXES = {
  "New Lead": "New lead",
  "Nearby Lead": "Nearby lead",
  "Visit Requested": "Visit requested",
};

const LEAD_KINDS = ["lead", "visit", "nearby"];

export const FEED_FILTERS = [
  { id: "all", label: "All" },
  { id: "leads", label: "Leads" },
  { id: "updates", label: "GharSetu" },
];

export function readSeenAt(uid) {
  try {
    return Number(localStorage.getItem(SEEN_KEY_PREFIX + uid)) || 0;
  } catch {
    return 0;
  }
}

export function writeSeenAt(uid, time) {
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + uid, String(time));
  } catch {
    // Storage blocked: the badge simply comes back on the next visit.
  }
}

const toMillis = (ts) => (ts?.toMillis ? ts.toMillis() : (ts?.seconds || 0) * 1000);

/** A broadcast a dealer should see: not tenant-only, and actually sent. */
export function isBroadcastForDealers(doc) {
  if ((doc.targetSegment || "All Registered Users") === "Tenants Only") return false;
  return !doc.status || DELIVERED.has(doc.status);
}

function personalKind(doc) {
  if (doc.type === "nearby_lead") return "nearby";
  if (doc.type === "lead") return /visit/i.test(doc.title || "") ? "visit" : "lead";
  if (doc.type === "saved_search_alert") return "alert";
  return "update";
}

// Broadcast links are app routes or web URLs; only web URLs open from the portal.
function webLink(deepLink) {
  const link = typeof deepLink === "string" ? deepLink.trim() : "";
  return /^https?:\/\//i.test(link) ? link : null;
}

/** Newest first. Lead alerts carry the lead to open; broadcasts their link. */
export function buildFeed(personalDocs, broadcastDocs) {
  const personal = personalDocs.map((doc) => {
    const kind = personalKind(doc);
    return {
      key: `p:${doc.id}`,
      kind,
      title: TITLE_FIXES[doc.title] || doc.title || "Notification",
      body: doc.body || "",
      time: toMillis(doc.sentAt),
      read: doc.read === true || doc.isRead === true,
      fromGharSetu: false,
      lead: LEAD_KINDS.includes(kind) && doc.leadId
        ? { leadId: doc.leadId, view: kind === "nearby" ? "nearby" : "direct" }
        : null,
      link: null,
    };
  });

  const broadcasts = broadcastDocs.filter(isBroadcastForDealers).map((doc) => ({
    key: `b:${doc.id}`,
    kind: "broadcast",
    title: doc.title || "Update from GharSetu",
    body: doc.body || "",
    time: toMillis(doc.sentAt),
    read: false,
    fromGharSetu: true,
    lead: null,
    link: webLink(doc.deepLink),
  }));

  return [...personal, ...broadcasts].sort((a, b) => b.time - a.time);
}

export function filterFeed(feed, filterId) {
  if (filterId === "leads") return feed.filter((item) => LEAD_KINDS.includes(item.kind));
  if (filterId === "updates") return feed.filter((item) => item.fromGharSetu);
  return feed;
}

export const isUnseen = (item, seenAt) => !item.read && item.time > seenAt;

const startOfDay = (time) => new Date(time).setHours(0, 0, 0, 0);

export function dayLabel(time, now = Date.now()) {
  if (!time) return "Earlier";
  const days = Math.round((startOfDay(now) - startOfDay(time)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  const date = new Date(time);
  if (days < 7) return date.toLocaleDateString(undefined, { weekday: "long" });
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
}

/** Sorted items in day groups: Today, Yesterday, weekday names, then dates. */
export function groupByDay(items, now = Date.now()) {
  return items.reduce((groups, item) => {
    const label = dayLabel(item.time, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
    return groups;
  }, []);
}

/** "12 min ago" within today; the clock time on earlier days (the group names the day). */
export function timeLabel(time, now = Date.now()) {
  if (!time) return "";
  if (dayLabel(time, now) === "Today") {
    const minutes = Math.floor((now - time) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    return `${Math.floor(minutes / 60)} h ago`;
  }
  return new Date(time).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
